/**
 * API для настроек уведомлений пациентов (TODO-052)
 * Дата: 10.02.2026
 * Связан с: internal/handler/patient_prefs.go, /api/patient-preferences
 */
import { apiClient } from './client';

/** Доступность каналов у пациента */
export interface PatientAvailableChannels {
  phone?: boolean;
  whatsapp_waba?: boolean;
  whatsapp_regular?: boolean;
  telegram_bot?: boolean;
  telegram_client?: boolean;
  max?: boolean;
  sms?: boolean;
  sms_tel?: boolean;
}

export interface PatientChannelCapability {
  available: boolean;
  source?: string;
  status?: string;
  updated_at?: string;
}

/** Пациент с каналами (для списка и деталей) */
export interface PatientWithChannelsDTO {
  id: string;
  external_id?: string;
  full_name?: string;
  phone_normalized: string;
  /** Алиас для отображения (phone_normalized) */
  phone?: string;
  last_visit?: string;
  last_visit_at?: string;
  preferred_channel?: string | null;
  preferred_channel_source_code?: string | null;
  preferred_channel_source_label?: string | null;
  preferred_channel_set_at?: string | null;
  contact_mode?: string;
  contact_mode_source_code?: string | null;
  contact_mode_source_label?: string | null;
  contact_mode_set_at?: string | null;
  available_channels: PatientAvailableChannels;
  channel_details?: Record<string, PatientChannelCapability>;
  privacy_restricted_channels?: Record<string, boolean>;
  messengers?: { messenger: string; messenger_id?: string; linked_at?: string }[];
}

export interface PatientPreferenceSourceDTO {
  code: string;
  display_name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  is_patient_selectable: boolean;
}

export interface PatientPreferredChannelHistoryItemDTO {
  id: string;
  patient_id: string;
  old_channel?: string | null;
  new_channel?: string | null;
  old_contact_mode?: string | null;
  new_contact_mode?: string | null;
  source_code: string;
  source_label?: string | null;
  actor_type: string;
  actor_id?: string | null;
  actor_name?: string | null;
  note?: string | null;
  created_at: string;
}

export interface PatientPrefsFilters {
  search?: string;
  last_visit_from?: string;
  last_visit_to?: string;
  match_mode?: 'any' | 'all';
  channels?: string[];
  exclude_channels?: string[];
  preferred_channel?: string;
  contact_mode?: string;
  offset?: number;
  limit?: number;
}

export interface PatientPrefsListResponse {
  patients: PatientWithChannelsDTO[];
  total: number;
  offset: number;
  limit: number;
}

export interface UpdatePreferredChannelResponse {
  success: boolean;
  patient_id: string;
  preferred_channel: string | null;
  preferred_channel_source_code?: string | null;
  preferred_channel_source_label?: string | null;
  preferred_channel_set_at?: string | null;
}

export const patientsApi = {
  /** Список пациентов с фильтрами */
  list: async (filters: PatientPrefsFilters = {}): Promise<PatientPrefsListResponse> => {
    const params: Record<string, string> = {};
    if (filters.search) params.search = filters.search;
    if (filters.last_visit_from) params.last_visit_from = filters.last_visit_from;
    if (filters.last_visit_to) params.last_visit_to = filters.last_visit_to;
    if (filters.match_mode) params.match_mode = filters.match_mode;
    if (filters.channels?.length) params.channels = filters.channels.join(',');
    if (filters.exclude_channels?.length) params.exclude_channels = filters.exclude_channels.join(',');
    if (filters.preferred_channel) params.preferred_channel = filters.preferred_channel;
    if (filters.contact_mode) params.contact_mode = filters.contact_mode;
    if (filters.offset !== undefined) params.offset = String(filters.offset);
    if (filters.limit !== undefined) params.limit = String(filters.limit);

    const response = await apiClient.get<PatientPrefsListResponse>('/patient-preferences', { params });
    return response.data;
  },

  /** Получить пациента по ID */
  get: async (id: string): Promise<PatientWithChannelsDTO> => {
    const response = await apiClient.get<PatientWithChannelsDTO>(`/patient-preferences/${id}`);
    return response.data;
  },

  /** Обновить preferred_channel (null = Auto) */
  updatePreferredChannel: async (
    id: string,
    channel: string | null,
    sourceCode: string,
    note?: string,
    contactMode?: string | null
  ): Promise<UpdatePreferredChannelResponse> => {
    const response = await apiClient.patch<UpdatePreferredChannelResponse>(
      `/patient-preferences/${id}/preferred-channel`,
      {
        channel,
        source_code: sourceCode,
        note,
        ...(contactMode !== undefined ? { contact_mode: contactMode } : {}),
      }
    );
    return response.data;
  },

  listPreferenceSources: async (): Promise<PatientPreferenceSourceDTO[]> => {
    const response = await apiClient.get<{ items: PatientPreferenceSourceDTO[] }>('/patient-preferences/sources');
    return response.data.items || [];
  },

  listPreferredChannelHistory: async (
    id: string,
    limit = 20
  ): Promise<PatientPreferredChannelHistoryItemDTO[]> => {
    const response = await apiClient.get<{ items: PatientPreferredChannelHistoryItemDTO[] }>(
      `/patient-preferences/${id}/history`,
      { params: { limit } }
    );
    return response.data.items || [];
  },
};
